// frontend/src/features/sites/sitesSlice.js
/*
Phase 8: Sites read-models are role-distinct:
- companySites: sites the company owns (UPS view)
- agencyAllowedSites: sites the agency is permitted to operate on (contract view)

selectedSiteId is global, but must remain valid for whichever list is active.
*/

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchMyCompanySites, fetchMyAgencyAllowedSites } from '../../services/siteService';

export const loadMyCompanySites = createAsyncThunk(
  'sites/loadMyCompanySites',
  async () => {
    return await fetchMyCompanySites();
  }
);

export const loadMyAgencyAllowedSites = createAsyncThunk(
  'sites/loadMyAgencyAllowedSites',
  async () => {
    return await fetchMyAgencyAllowedSites();
  }
);

const initialState = {
  companySites: [],
  agencyAllowedSites: [],
  selectedSiteId: null,
  loading: false,
  error: null,
};

function coerceId(v) {
  const n = v == null ? null : Number(v);
  return Number.isFinite(n) ? n : null;
}

function ensureSelectedIsValid(state, sites) {
  // If nothing selected, default to first
  if (!state.selectedSiteId && sites.length > 0) {
    state.selectedSiteId = coerceId(sites[0]?.id);
    return;
  }

  // If selected is no longer valid, reset to first (or null)
  if (
    state.selectedSiteId &&
    !sites.some((s) => coerceId(s.id) === state.selectedSiteId)
  ) {
    state.selectedSiteId = coerceId(sites[0]?.id);
  }
}

const sitesSlice = createSlice({
  name: 'sites',
  initialState,
  reducers: {
    setSelectedSiteId(state, action) {
      state.selectedSiteId = coerceId(action.payload);
    },

    // ✅ Phase-8 hardening: clear both read-models on logout/session switch
    resetSites(state) {
      state.companySites = [];
      state.agencyAllowedSites = [];
      state.selectedSiteId = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers(builder) {
    builder
      // ---------------------------
      // Company sites (UPS users)
      // ---------------------------
      .addCase(loadMyCompanySites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMyCompanySites.fulfilled, (state, action) => {
        state.loading = false;
        state.companySites = Array.isArray(action.payload) ? action.payload : [];
        ensureSelectedIsValid(state, state.companySites);
      })
      .addCase(loadMyCompanySites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Failed to load company sites';
      })

      // ---------------------------
      // Agency allowed sites (contract-aware)
      // ---------------------------
      .addCase(loadMyAgencyAllowedSites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMyAgencyAllowedSites.fulfilled, (state, action) => {
        state.loading = false;
        state.agencyAllowedSites = Array.isArray(action.payload) ? action.payload : [];
        ensureSelectedIsValid(state, state.agencyAllowedSites);
      })
      .addCase(loadMyAgencyAllowedSites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Failed to load agency allowed sites';
      });
  },
});

export const { setSelectedSiteId, resetSites } = sitesSlice.actions;
export default sitesSlice.reducer;

// Selectors
export const selectCompanySites = (s) => s.sites?.companySites || [];
export const selectAgencyAllowedSites = (s) => s.sites?.agencyAllowedSites || [];
export const selectSelectedSiteId = (s) => s.sites?.selectedSiteId ?? null;
export const selectSitesLoading = (s) => !!s.sites?.loading;
export const selectSitesError = (s) => s.sites?.error || null;
